import React, { Fragment, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
    AreaCard,
    AreaFormModal,
    Button,
    ConfirmationModal,
    CopyShiftAllocationsModal,
    DuplicateAreaModal,
    DuplicateEventModal,
    DuplicateShiftModal,
    EventFormModal,
    EventHeader,
    LoadingSpinner,
    Modal,
    ShiftAssignmentModal,
    ShiftFormModal,
    VolunteerAreaModal,
    VolunteerEventModal
} from '../components'
import { DuplicateShiftOptions } from '../components/DuplicateShiftModal'
import { DuplicateAreaOptions } from '../components/DuplicateAreaModal'
import {
    API_BASE_URL,
    AreaFormModalMeta,
    ConfirmationModalMeta,
    CopyShiftAllocationsModalMeta,
    DeleteAreaMeta,
    DeleteEventMeta,
    DeleteShiftMeta,
    DuplicateAreaModalMeta,
    DuplicateEventModalMeta,
    DuplicateShiftModalMeta,
    EventFormModalMeta,
    LoadingSpinnerMeta,
    ShiftAssignmentFormMeta,
    ShiftFormModalMeta,
    UpdateAreaFormMeta,
    UpdateEventFormMeta,
    UpdateShiftFormMeta,
    VolunteerAllocationViewTableMeta
} from '../data'
import { AccessColor, Area, Duty, EmailCampaign, Event, EventDetailMetaProps, Shift, ShiftWithDuty } from '../data/types'
import { ChevronLeftIcon, DeleteIcon, DownloadIcon, DuplicateIcon, EditIcon, PlusIcon } from '../icons'
import { useResourcePermissions } from '../hooks/useResourcePermissions'
import { useAuth } from './auth/AuthContext'
import { formatDateOnly } from '../utils/dates'

type Props = {
    meta: EventDetailMetaProps
}

type ShiftStatusExportTarget =
    | { type: 'event' }
    | { type: 'area', area: Area }

export const EventDetail = (props: Props) => {
    const { meta } = props
    const { eventId } = useParams<{ eventId: string }>()
    const navigate = useNavigate()
    const { user } = useAuth()
    const { canEditEvent, canDeleteEvent, canCreateArea, canEditArea, canDeleteArea, canCreateShift, canEditShift, canDeleteShift, canViewAllocations, canManageAllocations, canManageVisibility } = useResourcePermissions()
    const isAdmin = user?.role === 'admin'
    const [loading, setLoading] = useState(true)
    const [event, setEvent] = useState<Event | null>(null)
    const [areas, setAreasRaw] = useState<Area[]>([])
    const [shifts, setShifts] = useState<Record<number, ShiftWithDuty[]>>({})
    const [duties, setDuties] = useState<Duty[]>([])
    const [expandedAreas, setExpandedAreas] = useState<Set<number>>(new Set())
    const [error, setError] = useState<string | null>(null)
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)
    const [isEditModalOpen, setIsEditModalOpen] = useState(false)
    const [isCreateAreaModalOpen, setIsCreateAreaModalOpen] = useState(false)
    const [isEditAreaModalOpen, setIsEditAreaModalOpen] = useState(false)
    const [isDeleteAreaModalOpen, setIsDeleteAreaModalOpen] = useState(false)
    const [isCreateShiftModalOpen, setIsCreateShiftModalOpen] = useState(false)
    const [isEditShiftModalOpen, setIsEditShiftModalOpen] = useState(false)
    const [isDeleteShiftModalOpen, setIsDeleteShiftModalOpen] = useState(false)
    const [selectedArea, setSelectedArea] = useState<Area | null>(null)
    const [selectedShift, setSelectedShift] = useState<ShiftWithDuty | null>(null)
    const [isDeletingArea, setIsDeletingArea] = useState(false)
    const [isDeletingShift, setIsDeletingShift] = useState(false)
    const [isAssignShiftModalOpen, setIsAssignShiftModalOpen] = useState(false)
    const [isViewShiftModalOpen, setIsViewShiftModalOpen] = useState(false)
    const [isVolunteerAreaModalOpen, setIsVolunteerAreaModalOpen] = useState(false)
    const [isVolunteerEventModalOpen, setIsVolunteerEventModalOpen] = useState(false)
    const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false)
    const [isDuplicating, setIsDuplicating] = useState(false)
    const [isDuplicateAreaModalOpen, setIsDuplicateAreaModalOpen] = useState(false)
    const [isDuplicateShiftModalOpen, setIsDuplicateShiftModalOpen] = useState(false)
    const [isCopyShiftAllocationsModalOpen, setIsCopyShiftAllocationsModalOpen] = useState(false)
    const [isExportingEventShiftStatus, setIsExportingEventShiftStatus] = useState(false)
    const [exportingAreaShiftStatusId, setExportingAreaShiftStatusId] = useState<number | null>(null)
    const [emailCampaigns, setEmailCampaigns] = useState<EmailCampaign[]>([])
    const [loadingEmailCampaigns, setLoadingEmailCampaigns] = useState(false)
    const [selectedExportCampaignIds, setSelectedExportCampaignIds] = useState<number[]>([])
    const [shiftStatusExportTarget, setShiftStatusExportTarget] = useState<ShiftStatusExportTarget | null>(null)

    const setAreas = (update: Area[] | ((prevAreas: Area[]) => Area[])) => {
        const newAreas = typeof update === 'function' ? update(areas) : update
        // Sort areas by name
        const sortedAreas = newAreas.sort((a, b) => a.name.localeCompare(b.name))
        setAreasRaw(sortedAreas)
    }

    const fetchEventAndAreas = async () => {
        if (!eventId) return

        setLoading(true)
        setError(null)

        try {
            // Fetch duties first
            let dutiesMap: Record<number, string> = {}
            const dutiesResponse = await fetch(`${API_BASE_URL}/duty`, { credentials: 'include' })
            if (dutiesResponse.ok) {
                const dutiesResult = await dutiesResponse.json()
                const dutiesArray: Duty[] = dutiesResult.value || dutiesResult
                setDuties(dutiesArray)
                dutiesArray.forEach((duty: Duty) => {
                    dutiesMap[duty.id] = duty.name
                })
            }

            // Fetch event details
            const eventResponse = await fetch(`${API_BASE_URL}/event/${eventId}`, { credentials: 'include' })
            if (!eventResponse.ok) {
                throw new Error(`HTTP error! status: ${eventResponse.status}`)
            }
            const eventResult = await eventResponse.json()
            const events = eventResult.value || eventResult
            const eventData = Array.isArray(events) ? events.find((e: Event) => e.id === parseInt(eventId)) : eventResult
            setEvent(eventData)

            // Fetch areas for this event
            const areasResponse = await fetch(`${API_BASE_URL}/area/${eventId}`, { credentials: 'include' })
            if (!areasResponse.ok) {
                throw new Error(`HTTP error! status: ${areasResponse.status}`)
            }
            const areasResult = await areasResponse.json()
            let areasData: Area[] = areasResult.value || areasResult
            // Handle double-nested array structure
            if (Array.isArray(areasData) && areasData.length > 0 && Array.isArray(areasData[0])) {
                areasData = areasData[0]
            }
            setAreas(areasData)

            // Fetch shifts for all areas and expand them
            const areaIds = areasData.map((area: Area) => area.id)
            setExpandedAreas(new Set([]))

            const shiftsPromises = areaIds.map(async (areaId: number) => {
                try {
                    const shiftsResponse = await fetch(`${API_BASE_URL}/shift/${areaId}`, { credentials: 'include' })
                    if (shiftsResponse.ok) {
                        const shiftsResult = await shiftsResponse.json()
                        let shiftsData: Shift[] = shiftsResult.value || shiftsResult
                        // Handle double-nested array structure
                        if (Array.isArray(shiftsData) && shiftsData.length > 0 && Array.isArray(shiftsData[0])) {
                            shiftsData = shiftsData[0]
                        }
                        console.log("Shfit: ", shiftsData);
                        return { areaId, shiftsData }
                    }
                    return { areaId, shiftsData: [] }
                } catch (error) {
                    console.error(`Error fetching shifts for area ${areaId}:`, error)
                    return { areaId, shiftsData: [] }
                }
            })

            const allShifts = await Promise.all(shiftsPromises)
            const shiftsMap: Record<number, ShiftWithDuty[]> = {}
            allShifts.forEach(({ areaId, shiftsData }) => {
                shiftsMap[areaId] = shiftsData.map((shift: Shift) => ({
                    ...shift,
                    duty_name: dutiesMap[shift.duty_id] || 'Unknown Duty'
                }))
            })
            setShifts(shiftsMap)
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchEventAndAreas()
    }, [eventId])

    const fetchEmailCampaigns = async () => {
        if (emailCampaigns.length > 0) return

        setLoadingEmailCampaigns(true)
        try {
            const response = await fetch(`${API_BASE_URL}/email-campaigns`, {
                credentials: 'include'
            })

            if (!response.ok) {
                throw new Error(meta.emailCampaignLoadError)
            }

            const result = await response.json()
            let campaigns = result.value || result

            if (Array.isArray(campaigns) && campaigns.length > 0 && Array.isArray(campaigns[0])) {
                campaigns = campaigns[0]
            }

            setEmailCampaigns(Array.isArray(campaigns) ? campaigns : [])
        } catch (err) {
            setError(err instanceof Error ? err.message : meta.emailCampaignLoadError)
        } finally {
            setLoadingEmailCampaigns(false)
        }
    }


    const toggleAreaExpansion = (areaId: number) => {
        const newExpanded = new Set(Array.from(expandedAreas))

        if (newExpanded.has(areaId)) {
            newExpanded.delete(areaId)
        } else {
            newExpanded.add(areaId)
        }
        setExpandedAreas(newExpanded)
    }

    const handleDeleteClick = () => {
        setIsDeleteModalOpen(true)
    }

    const handleDeleteConfirm = async () => {
        if (!eventId) return

        setIsDeleting(true)
        try {
            const response = await fetch(`${API_BASE_URL}/event/${eventId}`, {
                method: 'DELETE',
                headers: {
                    'Accept': 'application/json'
                },
                credentials: 'include'
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            navigate('/events')
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to delete event')
            setIsDeleteModalOpen(false)
        } finally {
            setIsDeleting(false)
        }
    }

    const handleDeleteCancel = () => {
        setIsDeleteModalOpen(false)
    }

    const handleEditClick = () => {
        setIsEditModalOpen(true)
    }

    const handleDuplicateClick = () => {
        setIsDuplicateModalOpen(true)
    }

    const handleDuplicateEvent = async (overrides: any) => {
        if (!eventId) return

        setIsDuplicating(true)
        try {
            const response = await fetch(`${API_BASE_URL}/event/${eventId}/duplicate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify(overrides)
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            const result = await response.json()

            // Navigate to the new duplicated event
            if (result.model && result.model.id) {
                navigate(`/events/${result.model.id}`)
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to duplicate event')
            throw err
        } finally {
            setIsDuplicating(false)
        }
    }

    const handleCreateAreaClick = () => {
        setIsCreateAreaModalOpen(true)
    }

    const handleCreateArea = async (areaData: {
        name: string,
        leadName: string,
        leadPhoneNumber: string,
        deputyLeadName: string,
        deputyLeadPhoneNumber: string
        required_volunteer_count: number
        lead_csid: number
        deputy_lead_csid: number
        access_color_id: number
        lead_walkie_talkie: boolean
        deputy_lead_walkie_talkie: boolean
        shifts_visible: boolean
    }) => {
        if (!eventId) return

        const response = await fetch(`${API_BASE_URL}/area/${eventId}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({
                name: areaData.name,
                lead_name: areaData.leadName?.trim().length === 0 ? null : areaData.leadName,
                lead_phone_number: areaData.leadPhoneNumber?.trim().length === 0 ? null : areaData.leadPhoneNumber,
                deputy_lead_name: areaData.deputyLeadName?.trim().length === 0 ? null : areaData.deputyLeadName,
                deputy_lead_phone_number: areaData.deputyLeadPhoneNumber?.trim().length === 0 ? null : areaData.deputyLeadPhoneNumber,
                required_volunteer_count: areaData.required_volunteer_count,
                lead_csid: areaData.lead_csid,
                deputy_lead_csid: areaData.deputy_lead_csid,
                access_color_id: areaData.access_color_id,
                lead_walkie_talkie: areaData.lead_walkie_talkie,
                deputy_lead_walkie_talkie: areaData.deputy_lead_walkie_talkie,
                shifts_visible: areaData.shifts_visible
            })
        })

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }

        const result = await response.json()

        // Add the new area to the state
        if (result.model) {
            setAreas(prev => [...prev, result.model])
            // Initialize empty shifts array for the new area
            setShifts(prev => ({ ...prev, [result.model.id]: [] }))
        }
    }

    const handleEditAreaClick = (area: Area) => {
        setSelectedArea(area)
        setIsEditAreaModalOpen(true)
    }

    const handleUpdateArea = async (areaData: {
        name: string,
        leadName: string,
        leadPhoneNumber: string,
        deputyLeadName: string,
        deputyLeadPhoneNumber: string
        required_volunteer_count: number
        lead_csid: number
        deputy_lead_csid: number
        access_color_id: number
        lead_walkie_talkie: boolean,
        deputy_lead_walkie_talkie: boolean,
        shifts_visible: boolean

    }) => {
        if (!eventId || !selectedArea) return

        const response = await fetch(`${API_BASE_URL}/area/${eventId}/${selectedArea.id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({
                name: areaData.name,
                lead_name: areaData.leadName?.trim().length === 0 ? null : areaData.leadName,
                lead_phone_number: areaData?.leadPhoneNumber.trim().length === 0 ? null : areaData.leadPhoneNumber,
                deputy_lead_name: areaData?.deputyLeadName.trim().length === 0 ? null : areaData.deputyLeadName,
                deputy_lead_phone_number: areaData?.deputyLeadPhoneNumber.trim().length === 0 ? null : areaData.deputyLeadPhoneNumber,
                required_volunteer_count: areaData.required_volunteer_count,
                lead_csid: areaData.lead_csid,
                deputy_lead_csid: areaData.deputy_lead_csid,
                access_color_id: areaData.access_color_id,
                lead_walkie_talkie: areaData.lead_walkie_talkie,
                deputy_lead_walkie_talkie: areaData.deputy_lead_walkie_talkie,
                shifts_visible: areaData.shifts_visible
            })
        })

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }

        const result = await response.json()

        // Update the area in state
        if (result.model) {
            setAreas(prev => prev.map(area =>
                area.id === result.model.id ? result.model : area
            ))
        }
    }

    const handleDeleteAreaClick = (area: Area) => {
        setSelectedArea(area)
        setIsDeleteAreaModalOpen(true)
    }

    const handleDeleteAreaConfirm = async () => {
        if (!eventId || !selectedArea) return

        setIsDeletingArea(true)
        try {
            const response = await fetch(`${API_BASE_URL}/area/${eventId}/${selectedArea.id}`, {
                method: 'DELETE',
                headers: {
                    'Accept': 'application/json'
                },
                credentials: 'include'
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            // Remove the area from state
            setAreas(prev => prev.filter(area => area.id !== selectedArea.id))
            // Remove shifts for this area
            setShifts(prev => {
                const newShifts = { ...prev }
                delete newShifts[selectedArea.id]
                return newShifts
            })
            // Remove from expanded areas
            setExpandedAreas(prev => {
                const newExpanded = new Set(Array.from(prev))
                newExpanded.delete(selectedArea.id)
                return newExpanded
            })

            setIsDeleteAreaModalOpen(false)
            setSelectedArea(null)
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to delete area')
            setIsDeleteAreaModalOpen(false)
        } finally {
            setIsDeletingArea(false)
        }
    }

    const handleDeleteAreaCancel = () => {
        setIsDeleteAreaModalOpen(false)
        setSelectedArea(null)
    }

    const handleDuplicateAreaClick = (area: Area) => {
        setSelectedArea(area)
        setIsDuplicateAreaModalOpen(true)
    }

    const handleDuplicateAreaSubmit = async (options: DuplicateAreaOptions) => {
        if (!eventId || !selectedArea) return

        try {
            const response = await fetch(`${API_BASE_URL}/area/${eventId}/${selectedArea.id}/duplicate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({
                    count: options.count,
                    include_allocations: options.includeAllocations,
                    target_event_id: options.targetEventId
                })
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            const result = await response.json()
            const targetEventId = options.targetEventId ?? parseInt(eventId)

            if (targetEventId === parseInt(eventId) && result.models && Array.isArray(result.models)) {
                const duplicatedAreas = result.models as Area[]

                setAreas(prev => [...prev, ...duplicatedAreas])
                setShifts(prev => {
                    const nextShifts = { ...prev }
                    duplicatedAreas.forEach((area) => {
                        nextShifts[area.id] = (area.shifts || []).map((shift) => ({
                            ...shift,
                            duty_name: shift.duty?.name || duties.find(duty => duty.id === shift.duty_id)?.name || 'Unknown Duty'
                        }))
                    })
                    return nextShifts
                })
            }

            setIsDuplicateAreaModalOpen(false)
            setSelectedArea(null)
        } catch (err) {
            throw new Error(err instanceof Error ? err.message : 'Failed to duplicate area')
        }
    }

    const handleAddShiftClick = (area: Area) => {
        setSelectedArea(area)
        setIsCreateShiftModalOpen(true)
    }

    const handleCreateShift = async (shiftData: {
        duty_id: number,
        duty_name: string,
        title: string,
        start_time: string,
        end_time: string,
        leadName: string,
        leadPhoneNumber: string,
        deputyLeadName: string,
        deputyLeadPhoneNumber: string,
        required_volunteer_count: number
        lead_csid: number,
        deputy_lead_csid: number
        access_color_id: number
        lead_walkie_talkie: boolean,
        deputy_lead_walkie_talkie: boolean
        criteria: string
        instruction: string
    }) => {
        if (!selectedArea) return;

        if (shiftData.title === '') {
            const areaShifts = shifts[selectedArea.id] || [];

            const existingCount = areaShifts.filter(s => s.duty_id === shiftData.duty_id).length;

            shiftData.title = `${shiftData.duty_name}-${existingCount + 1}`;
        }


        const response = await fetch(`${API_BASE_URL}/shift/${selectedArea.id}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({
                duty: [shiftData.duty_id],
                start_time: [shiftData.start_time],
                end_time: [shiftData.end_time],
                lead_name: [shiftData.leadName?.trim().length === 0 ? null : shiftData.leadName],
                lead_phone_number: [shiftData.leadPhoneNumber?.trim().length === 0 ? null : shiftData.leadPhoneNumber],
                deputy_lead_name: [shiftData.deputyLeadName?.trim().length === 0 ? null : shiftData.deputyLeadName],
                deputy_lead_phone_number: [shiftData.deputyLeadPhoneNumber?.trim().length === 0 ? null : shiftData.deputyLeadPhoneNumber],
                required_volunteer_count: [shiftData.required_volunteer_count],
                lead_csid: [shiftData.lead_csid],
                deputy_lead_csid: [shiftData.deputy_lead_csid],
                access_color_id: [shiftData.access_color_id],
                lead_walkie_talkie: [shiftData.lead_walkie_talkie],
                deputy_lead_walkie_talkie: [shiftData.deputy_lead_walkie_talkie],
                criteria: [shiftData.criteria],
                title: [shiftData.title],
                instruction: [shiftData.instruction]
            })
        })

        if (!response.ok) {
            console.log(response.text());
            throw new Error(`HTTP error! status: ${response.status}`)
        }

        const result = await response.json()

        // Add the new shift(s) to the state
        if (result.models && Array.isArray(result.models) && result.models.length > 0) {
            const newShift = result.models[0]
            const dutyName = duties.find(d => d.id === newShift.duty_id)?.name || 'Unknown Duty'
            const shiftWithDuty: ShiftWithDuty = {
                ...newShift,
                duty_name: dutyName
            }
            setShifts(prev => ({
                ...prev,
                [selectedArea.id]: [...(prev[selectedArea.id] || []), shiftWithDuty]
            }))
        }
    }

    const handleEditShiftClick = (area: Area, shift: ShiftWithDuty) => {
        setSelectedArea(area)
        setSelectedShift(shift)
        setIsEditShiftModalOpen(true)
    }

    const handleUpdateShift = async (shiftData: {
        duty_id: number,
        duty_name: string
        start_time: string,
        end_time: string,
        leadName: string,
        leadPhoneNumber: string,
        deputyLeadName: string,
        deputyLeadPhoneNumber: string
        required_volunteer_count: number
        lead_csid: number
        deputy_lead_csid: number
        access_color_id: number
        lead_walkie_talkie: boolean,
        deputy_lead_walkie_talkie: boolean
        criteria: string
        title: string
        instruction: string
    }) => {
        if (!selectedArea || !selectedShift) return

        if (shiftData.title === '') {
            const areaShifts = shifts[selectedArea.id] || [];

            const existingCount = areaShifts.filter(s => (s.duty_id === shiftData.duty_id) && (s.id !== selectedShift.id)).length;

            shiftData.title = `${shiftData.duty_name}-${existingCount + 1}`;
        }


        const response = await fetch(`${API_BASE_URL}/shift/${selectedArea.id}/${selectedShift.id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({
                duty_id: shiftData.duty_id,
                start_time: shiftData.start_time,
                end_time: shiftData.end_time,
                lead_name: shiftData.leadName?.trim().length === 0 ? null : shiftData.leadName,
                lead_phone_number: shiftData?.leadPhoneNumber.trim().length === 0 ? null : shiftData.leadPhoneNumber,
                deputy_lead_name: shiftData?.deputyLeadName.trim().length === 0 ? null : shiftData.deputyLeadName,
                deputy_lead_phone_number: shiftData?.deputyLeadPhoneNumber.trim().length === 0 ? null : shiftData.deputyLeadPhoneNumber,
                required_volunteer_count: shiftData?.required_volunteer_count,
                lead_csid: shiftData?.lead_csid,
                deputy_lead_csid: shiftData?.deputy_lead_csid,
                access_color_id: shiftData?.access_color_id,
                lead_walkie_talkie: shiftData?.lead_walkie_talkie,
                deputy_lead_walkie_talkie: shiftData?.deputy_lead_walkie_talkie,
                criteria: shiftData?.criteria,
                title: shiftData?.title,
                instruction: shiftData?.instruction
            })
        })

        if (!response.ok) {
            const text = await response.text()
            const errorText = JSON.parse(text)
            throw new Error(errorText.message);
        }

        const result = await response.json()

        // Update the shift in state
        if (result.model) {
            const dutyName = duties.find(d => d.id === result.model.duty_id)?.name || 'Unknown Duty'
            const updatedShift: ShiftWithDuty = {
                ...result.model,
                duty_name: dutyName
            }
            setShifts(prev => ({
                ...prev,
                [selectedArea.id]: prev[selectedArea.id].map(shift =>
                    shift.id === selectedShift.id ? updatedShift : shift
                )
            }))
        }
    }

    const handleDeleteShiftClick = (area: Area, shift: ShiftWithDuty) => {
        setSelectedArea(area)
        setSelectedShift(shift)
        setIsDeleteShiftModalOpen(true)
    }

    const handleDeleteShiftConfirm = async () => {
        if (!selectedArea || !selectedShift) return

        setIsDeletingShift(true)
        try {
            const response = await fetch(`${API_BASE_URL}/shift/${selectedArea.id}/${selectedShift.id}`, {
                method: 'DELETE',
                headers: {
                    'Accept': 'application/json'
                },
                credentials: 'include'
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            // Remove the shift from state
            setShifts(prev => ({
                ...prev,
                [selectedArea.id]: prev[selectedArea.id].filter(shift => shift.id !== selectedShift.id)
            }))

            setIsDeleteShiftModalOpen(false)
            setSelectedShift(null)
            setSelectedArea(null)
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to delete shift')
            setIsDeleteShiftModalOpen(false)
        } finally {
            setIsDeletingShift(false)
        }
    }

    const handleDeleteShiftCancel = () => {
        setIsDeleteShiftModalOpen(false)
        setSelectedShift(null)
        setSelectedArea(null)
    }

    const handleDuplicateShiftClick = (area: Area, shift: ShiftWithDuty) => {
        setSelectedArea(area)
        setSelectedShift(shift)
        setIsDuplicateShiftModalOpen(true)
    }

    const handleCopyShiftAllocationsClick = (area: Area, shift: ShiftWithDuty) => {
        setSelectedArea(area)
        setSelectedShift(shift)
        setIsCopyShiftAllocationsModalOpen(true)
    }

    const handleDuplicateShiftSubmit = async (options: DuplicateShiftOptions) => {
        if (!selectedArea || !selectedShift) return

        try {
            const response = await fetch(`${API_BASE_URL}/shift/${selectedArea.id}/${selectedShift.id}/duplicate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({
                    count: options.count,
                    include_allocations: options.includeAllocations,
                    target_area_id: options.targetAreaId
                })
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            const result = await response.json()

            // Determine where to add the duplicated shifts
            const targetAreaId = options.targetAreaId ?? selectedArea.id

            // Add the duplicated shifts to state
            if (result.models && Array.isArray(result.models) && result.models.length > 0) {
                const newShifts: ShiftWithDuty[] = result.models.map((shift: Shift) => {
                    const dutyName = duties.find(d => d.id === shift.duty_id)?.name || 'Unknown Duty'
                    return {
                        ...shift,
                        duty_name: dutyName
                    }
                })

                // Only update local state if duplicating within the current event
                if (options.targetAreaId === null || areas.some(a => a.id === targetAreaId)) {
                    setShifts(prev => ({
                        ...prev,
                        [targetAreaId]: [...(prev[targetAreaId] || []), ...newShifts]
                    }))
                }
            }

            setIsDuplicateShiftModalOpen(false)
            setSelectedShift(null)
            setSelectedArea(null)
        } catch (err) {
            throw new Error(err instanceof Error ? err.message : 'Failed to duplicate shift')
        }
    }

    const handleCopyShiftAllocationsSubmit = async (targetShiftId: number) => {
        if (!selectedShift) return

        const response = await fetch(`${API_BASE_URL}/allocation/shift/${selectedShift.id}/copy`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({
                target_shift_id: targetShiftId
            })
        })

        const result = await response.json().catch(() => null)

        if (!response.ok) {
            throw new Error(result?.message || 'Failed to copy shift allocations')
        }

        await fetchEventAndAreas()
    }

    const handleUpdateEvent = async (eventData: any) => {
        if (!eventId) return

        console.log(eventData)

        const response = await fetch(`${API_BASE_URL}/event/${eventId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify(eventData)
        })

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }

        const result = await response.json()

        // Update the event in state
        if (result.model) {
            setEvent(result.model)
        }
    }

    const formatTime = (timeStr: string | null | undefined) => {
        if (!timeStr) return 'N/A'
        const [hours, minutes] = timeStr.split(':')
        const hour = parseInt(hours)
        const ampm = hour >= 12 ? 'PM' : 'AM'
        const displayHour = hour % 12 || 12
        return `${displayHour}:${minutes} ${ampm}`
    }

    const formatDate = (dateStr: string) => {
        return formatDateOnly(dateStr, 'en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        })
    }

    const handleAssignShiftClick = (shift: ShiftWithDuty) => {
        setSelectedShift(shift);
        setIsAssignShiftModalOpen(true);
    }

    const handleViewShiftClick = (shift: ShiftWithDuty) => {
        setSelectedShift(shift);
        setIsViewShiftModalOpen(true);
    }

    const handleAssignShiftCloseClick = () => {
        setSelectedShift(null);
        setIsAssignShiftModalOpen(false);
    }

    const handleViewShiftCloseClick = () => {
        setSelectedShift(null);
        setIsViewShiftModalOpen(false);
    }

    const handleVolunteerEventClick = (event: Event) => {
        setIsVolunteerEventModalOpen(true);
    }

    const handleVolunteerEventClickClose = () => {
        setIsVolunteerEventModalOpen(false);
    }

    const handleVolunteerAreaClick = (area: Area) => {
        setSelectedArea(area)
        setIsVolunteerAreaModalOpen(true);
    }

    const handleVolunteerAreaClickClose = () => {
        setSelectedArea(null)
        setIsVolunteerAreaModalOpen(false);
    }

    const downloadShiftRegionStatus = async (url: string, filename: string, campaignIds: number[]) => {
        const params = new URLSearchParams()
        params.append('campaign_ids', JSON.stringify(campaignIds))

        const response = await fetch(`${url}?${params.toString()}`, {
            credentials: 'include'
        })

        if (!response.ok) {
            throw new Error(meta.exportShiftRegionStatusError)
        }

        const contentType = response.headers.get('content-type')
        if (contentType?.includes('application/json')) {
            const result = await response.json()
            throw new Error(result.message || meta.exportShiftRegionStatusError)
        }

        const blob = await response.blob()
        const urlObject = window.URL.createObjectURL(blob)
        const anchor = document.createElement('a')
        anchor.href = urlObject
        anchor.download = filename
        document.body.appendChild(anchor)
        anchor.click()
        window.URL.revokeObjectURL(urlObject)
        document.body.removeChild(anchor)
    }

    const openShiftStatusExportModal = async (target: ShiftStatusExportTarget) => {
        setSelectedExportCampaignIds([])
        setShiftStatusExportTarget(target)
        await fetchEmailCampaigns()
    }

    const closeShiftStatusExportModal = () => {
        if (isExportingEventShiftStatus || exportingAreaShiftStatusId !== null) return
        setShiftStatusExportTarget(null)
        setSelectedExportCampaignIds([])
    }

    const toggleSelectedExportCampaign = (campaignId: number) => {
        setSelectedExportCampaignIds(prev => (
            prev.includes(campaignId)
                ? prev.filter(id => id !== campaignId)
                : [...prev, campaignId]
        ))
    }

    const handleConfirmShiftStatusExport = async () => {
        if (!event || !shiftStatusExportTarget) return

        if (selectedExportCampaignIds.length === 0) {
            setError(meta.selectEmailCampaignsError)
            return
        }

        if (shiftStatusExportTarget.type === 'event') {
            setIsExportingEventShiftStatus(true)
        } else {
            setExportingAreaShiftStatusId(shiftStatusExportTarget.area.id)
        }

        try {
            if (shiftStatusExportTarget.type === 'event') {
                await downloadShiftRegionStatus(
                    `${API_BASE_URL}/allocation/event/${event.id}/shift-region-status-export`,
                    `event_${event.id}_shift_region_status_${new Date().toISOString().split('T')[0]}.csv`,
                    selectedExportCampaignIds
                )
            } else {
                await downloadShiftRegionStatus(
                    `${API_BASE_URL}/allocation/area/${shiftStatusExportTarget.area.id}/shift-region-status-export`,
                    `area_${shiftStatusExportTarget.area.id}_shift_region_status_${new Date().toISOString().split('T')[0]}.csv`,
                    selectedExportCampaignIds
                )
            }

            setShiftStatusExportTarget(null)
            setSelectedExportCampaignIds([])
        } catch (err) {
            setError(err instanceof Error ? err.message : meta.exportShiftRegionStatusError)
        } finally {
            setIsExportingEventShiftStatus(false)
            setExportingAreaShiftStatusId(null)
        }
    }


    const totalEventRequiredVolunteers = React.useMemo(() => {
        // Flatten all shifts from all areas into a single array
        const allShifts = Object.values(shifts).flat();
        return allShifts.reduce((sum, shift) => sum + (Number(shift.required_volunteer_count || 0)), 0);
    }, [shifts]);


    return (
        <Fragment>
            <div className="flex-1 bg-gray-50 dark:bg-gray-900 pt-8">
                <div className="container mx-auto px-4">
                    <div className="flex justify-between items-center mb-6">
                        <Link
                            to="/events"
                            className="inline-flex items-center text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                        >
                            <ChevronLeftIcon className="w-5 h-5 me-2" />
                            {meta.backToEventsButton}
                        </Link>
                        {!loading && !error && event && (
                            <div className="flex gap-3">
                                {canEditEvent(event) && (
                                    <Button onClick={handleEditClick} color="primary">
                                        <EditIcon className="w-5 h-5" />
                                        {meta.editEventButton}
                                    </Button>
                                )}
                                {canEditEvent(event) && (
                                    <Button onClick={handleDuplicateClick} variant="outline" color="primary">
                                        <DuplicateIcon className="w-5 h-5" />
                                        Duplicate
                                    </Button>
                                )}
                                {isAdmin && (
                                    <Button onClick={() => openShiftStatusExportModal({ type: 'event' })} variant="outline" color="primary" disabled={isExportingEventShiftStatus}>
                                        <DownloadIcon className="w-5 h-5" />
                                        {isExportingEventShiftStatus ? meta.exportingShiftRegionStatusButton : meta.exportShiftRegionStatusButton}
                                    </Button>
                                )}
                                {canDeleteEvent(event) && (
                                    <Button onClick={handleDeleteClick} color="danger">
                                        <DeleteIcon className="w-5 h-5" />
                                        {DeleteEventMeta.detailDeleteButtonText}
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>

                    {loading && (
                        <div className="flex justify-center items-center min-h-[400px]">
                            <LoadingSpinner meta={LoadingSpinnerMeta} />
                        </div>
                    )}

                    {error && (
                        <div className="max-w-2xl mx-auto">
                            <div
                                className="bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-6 py-4 rounded-lg">
                                <p className="font-bold text-lg mb-2">{meta.errorTitle}</p>
                                <p>{error}</p>
                            </div>
                        </div>
                    )}

                    {!loading && !error && event && (
                        <>
                            <EventHeader
                                event={{
                                    ...event,
                                    // allocation_count now includes leads from the backend
                                    required_volunteer_count: totalEventRequiredVolunteers
                                }}
                                meta={meta}
                                formatDate={formatDate}
                                formatTime={formatTime}
                                onVolunteerEventClick={handleVolunteerEventClick}
                                canViewEventAllocations={canViewAllocations(null, null, event)}
                            />

                            {/* Areas Section */}
                            <div className="mb-8">
                                <div className="flex justify-between items-center mb-6">
                                    <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                                        {meta.areasTitle}
                                    </h2>
                                    {canCreateArea(event) && (
                                        <Button onClick={handleCreateAreaClick} color="success">
                                            <PlusIcon className="w-5 h-5" />
                                            {meta.createAreaButton}
                                        </Button>
                                    )}
                                </div>

                                {areas.length === 0 ? (
                                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
                                        <p className="text-gray-500 dark:text-gray-400">{meta.noAreasMessage}</p>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {areas.map((area) => (
                                            <AreaCard
                                                key={area.id}
                                                area={area}
                                                shifts={shifts[area.id]}
                                                isExpanded={expandedAreas.has(area.id)}
                                                onToggle={() => toggleAreaExpansion(area.id)}
                                                onEdit={() => handleEditAreaClick(area)}
                                                onDelete={() => handleDeleteAreaClick(area)}
                                                onDuplicate={() => handleDuplicateAreaClick(area)}
                                                onAddShift={() => handleAddShiftClick(area)}
                                                onEditShift={(shift) => handleEditShiftClick(area, shift)}
                                                onDeleteShift={(shift) => handleDeleteShiftClick(area, shift)}
                                                onDuplicateShift={(shift) => handleDuplicateShiftClick(area, shift)}
                                                onCopyShiftAllocations={isAdmin ? (shift) => handleCopyShiftAllocationsClick(area, shift) : undefined}
                                                onAssignShift={handleAssignShiftClick}
                                                onViewShift={handleViewShiftClick}
                                                meta={meta}
                                                formatTime={formatTime}
                                                onVolunteerAreaClick={handleVolunteerAreaClick}
                                                onExportShiftRegionStatus={isAdmin ? (area) => openShiftStatusExportModal({ type: 'area', area }) : undefined}
                                                canEdit={canEditArea(area, event)}
                                                canDelete={canDeleteArea(area)}
                                                canDuplicate={canCreateArea(event)}
                                                canCreateShift={canCreateShift(area, event)}
                                                canEditShift={(shift) => canEditShift(shift, area, event)}
                                                canDeleteShift={(shift) => canDeleteShift(shift)}
                                                canDuplicateShift={(shift) => canCreateShift(area, event)}
                                                canCopyShiftAllocations={() => isAdmin}
                                                canViewAllocations={canViewAllocations(null, area, event)}
                                                isExportingShiftRegionStatus={exportingAreaShiftStatusId === area.id}
                                                canViewShiftAllocations={(shift) => canViewAllocations(shift, area, event) && !canManageAllocations(shift, area, event)}
                                                canManageAllocations={(shift) => canManageAllocations(shift, area, event)}
                                                eventShiftsVisible={event?.shifts_visible}
                                                showVisibility={canManageVisibility()}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>

            <DuplicateEventModal
                meta={DuplicateEventModalMeta}
                isOpen={isDuplicateModalOpen}
                onClose={() => setIsDuplicateModalOpen(false)}
                onSubmit={handleDuplicateEvent}
                originalEvent={event}
            />

            <Modal
                isOpen={shiftStatusExportTarget !== null}
                onClose={closeShiftStatusExportModal}
                title={meta.selectEmailCampaignsTitle}
                maxWidth="2xl"
                footer={(
                    <div className="flex justify-end gap-3">
                        <Button type="button" variant="outline" color="neutral" onClick={closeShiftStatusExportModal} disabled={isExportingEventShiftStatus || exportingAreaShiftStatusId !== null}>
                            {meta.selectEmailCampaignsCancelButton}
                        </Button>
                        <Button type="button" color="primary" onClick={handleConfirmShiftStatusExport} disabled={selectedExportCampaignIds.length === 0 || loadingEmailCampaigns || isExportingEventShiftStatus || exportingAreaShiftStatusId !== null}>
                            <DownloadIcon className="w-4 h-4" />
                            {isExportingEventShiftStatus || exportingAreaShiftStatusId !== null ? meta.exportingShiftRegionStatusButton : meta.selectEmailCampaignsExportButton}
                        </Button>
                    </div>
                )}
            >
                <div className="p-6 space-y-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        {meta.selectEmailCampaignsDescription}
                    </p>

                    {loadingEmailCampaigns ? (
                        <div className="flex justify-center py-8">
                            <LoadingSpinner meta={LoadingSpinnerMeta} />
                        </div>
                    ) : emailCampaigns.length === 0 ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400 py-4">
                            {meta.noEmailCampaignsMessage}
                        </p>
                    ) : (
                        <div className="max-h-96 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-200 dark:divide-gray-700">
                            {emailCampaigns.map(campaign => (
                                <label key={campaign.id} className="flex items-start gap-3 p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={selectedExportCampaignIds.includes(campaign.id)}
                                        onChange={() => toggleSelectedExportCampaign(campaign.id)}
                                        className="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                    />
                                    <span className="min-w-0 flex-1">
                                        <span className="block font-medium text-gray-900 dark:text-gray-100 truncate">
                                            {campaign.subject}
                                        </span>
                                        <span className="block text-xs text-gray-500 dark:text-gray-400 mt-1">
                                            {meta.emailCampaignOptionDetails(campaign.status, campaign.sent_count, campaign.created_at)}
                                        </span>
                                    </span>
                                </label>
                            ))}
                        </div>
                    )}
                </div>
            </Modal>

            <EventFormModal
                meta={{ ...EventFormModalMeta, ...UpdateEventFormMeta }}
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                onSubmit={handleUpdateEvent}
                initialEvent={event}
                canManageVisibility={canManageVisibility()}
            />

            <ConfirmationModal
                meta={ConfirmationModalMeta}
                isOpen={isDeleteModalOpen}
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
                title={DeleteEventMeta.confirmTitle}
                message={DeleteEventMeta.confirmMessage}
                isLoading={isDeleting}
            />

            <AreaFormModal
                meta={AreaFormModalMeta}
                isOpen={isCreateAreaModalOpen}
                onClose={() => setIsCreateAreaModalOpen(false)}
                onSubmit={handleCreateArea}
                canManageVisibility={canManageVisibility()}
            />

            <AreaFormModal
                meta={{ ...AreaFormModalMeta, ...UpdateAreaFormMeta }}
                isOpen={isEditAreaModalOpen}
                onClose={() => setIsEditAreaModalOpen(false)}
                onSubmit={handleUpdateArea}
                initialArea={selectedArea}
                canManageVisibility={canManageVisibility()}
            />

            <ConfirmationModal
                meta={ConfirmationModalMeta}
                isOpen={isDeleteAreaModalOpen}
                onConfirm={handleDeleteAreaConfirm}
                onCancel={handleDeleteAreaCancel}
                title={DeleteAreaMeta.confirmTitle}
                message={DeleteAreaMeta.confirmMessage}
                isLoading={isDeletingArea}
            />

            <DuplicateAreaModal
                meta={DuplicateAreaModalMeta}
                isOpen={isDuplicateAreaModalOpen}
                onClose={() => {
                    setIsDuplicateAreaModalOpen(false)
                    setSelectedArea(null)
                }}
                onSubmit={handleDuplicateAreaSubmit}
                areaName={selectedArea?.name ?? 'Area'}
                currentEventId={event?.id ?? parseInt(eventId ?? '0')}
            />

            <ShiftFormModal
                meta={ShiftFormModalMeta}
                isOpen={isCreateShiftModalOpen}
                onClose={() => {
                    setIsCreateShiftModalOpen(false)
                    setSelectedArea(null)
                }}
                onSubmit={handleCreateShift}
                duties={duties}
            />

            <ShiftFormModal
                meta={{ ...ShiftFormModalMeta, ...UpdateShiftFormMeta }}
                isOpen={isEditShiftModalOpen}
                onClose={() => {
                    setIsEditShiftModalOpen(false)
                    setSelectedShift(null)
                    setSelectedArea(null)
                }}
                onSubmit={handleUpdateShift}
                duties={duties}
                initialShift={selectedShift}
            />

            <ConfirmationModal
                meta={ConfirmationModalMeta}
                isOpen={isDeleteShiftModalOpen}
                onConfirm={handleDeleteShiftConfirm}
                onCancel={handleDeleteShiftCancel}
                title={DeleteShiftMeta.confirmTitle}
                message={DeleteShiftMeta.confirmMessage}
                isLoading={isDeletingShift}
            />

            <DuplicateShiftModal
                meta={DuplicateShiftModalMeta}
                isOpen={isDuplicateShiftModalOpen}
                onClose={() => {
                    setIsDuplicateShiftModalOpen(false)
                    setSelectedShift(null)
                    setSelectedArea(null)
                }}
                onSubmit={handleDuplicateShiftSubmit}
                shiftTitle={selectedShift?.title ?? selectedShift?.duty_name ?? 'Shift'}
                currentAreaId={selectedArea?.id ?? 0}
                currentEventId={event?.id ?? parseInt(eventId ?? '0')}
            />

            <CopyShiftAllocationsModal
                meta={CopyShiftAllocationsModalMeta}
                isOpen={isCopyShiftAllocationsModalOpen}
                sourceShift={selectedShift}
                onClose={() => {
                    setIsCopyShiftAllocationsModalOpen(false)
                    setSelectedShift(null)
                    setSelectedArea(null)
                }}
                onSubmit={handleCopyShiftAllocationsSubmit}
            />

            <ShiftAssignmentModal
                meta={ShiftAssignmentFormMeta}
                isOpen={isAssignShiftModalOpen}
                shift={selectedShift}
                onClose={handleAssignShiftCloseClick}
                onAssignment={fetchEventAndAreas} // TODO handle locally
                canManageAllocations={canManageAllocations(selectedShift, areas.filter((a) => a.id === selectedShift?.area_id)[0], event)}
            />

            <ShiftAssignmentModal
                meta={ShiftAssignmentFormMeta}
                isOpen={isViewShiftModalOpen}
                shift={selectedShift}
                onClose={handleViewShiftCloseClick}
                onAssignment={fetchEventAndAreas}
                readOnly={true}
                canManageAllocations={canManageAllocations(selectedShift, areas.filter((a) => a.id === selectedShift?.area_id)[0], event)}
            />

            <VolunteerEventModal
                meta={VolunteerAllocationViewTableMeta}
                event={event}
                isOpen={isVolunteerEventModalOpen}
                onCancel={handleVolunteerEventClickClose}
                title={`${event?.name} - Volunteer Information`}
                message="The following volunteers have been allocated in this event:"
            />

            <VolunteerAreaModal
                meta={VolunteerAllocationViewTableMeta}
                area={selectedArea}
                isOpen={isVolunteerAreaModalOpen}
                onCancel={handleVolunteerAreaClickClose}
                title={`${selectedArea?.name} - Volunteer Information`}
                message="The following volunteers have been allocated in this area:"
            />

        </Fragment>
    )
}
